import React from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import usePersistedId from '../utils/use-persisted-id';
import { useFormProvider } from '../form-provider';
import FormAlert from '../form-alert';
import { colors } from '../colors';
import throwOnMissing from '../utils/throw-on-missing';
import VisuallyHidden from '../visually-hidden';

export interface RadioProps
    extends Omit<HtmlPropsNoRef<HTMLInputElement>, 'value'> {
    /**
* Children refers to the label
* */
    children: any;
    /**
* The size of the radio button.
* */
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    /**
* Make the field disabled or not. Defaults to false for all field types.
 */
    disabled?: boolean;
    /**
* This refers to the value of the input
*/
    value: string | boolean;
}

export const RadioItem = ({
    size = 'sm',
    children,
    value,
    disabled = false,
}: RadioProps): JSX.Element => {
    throwOnMissing(value, 'value', 'RadioItem');
    const foundSize = {
        xs: '0.75rem',
        sm: '1rem',
        md: '1.5rem',
        lg: '2rem',
        xl: '3rem',
    }[size];
    const innerSize = {
        xs: '0.35rem',
        sm: '0.5rem',
        md: '0.75rem',
        lg: '1rem',
        xl: '1.5rem',
    }[size];
    const id = usePersistedId();

    return (
        <div css={{ display: 'flex', margin: '10px 0', alignItems: 'center' }}>
            <RadioGroupPrimitive.Item
                tabIndex={0}
                css={{
                    backgroundColor: colors.grey200,
                    border: `2px solid ${colors.grey400}`,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    width: `${foundSize} !important`,
                    height: `${foundSize} !important`,
                    borderRadius: '50%',
                    transition: 'all 0.3s',
                    '&:hover': { backgroundColor: colors.grey300 },
                    '&[data-state=checked], &:hover[data-state=checked]': {
                        borderColor: colors.primaryOrange,
                        backgroundColor: 'transparent',
                    },
                }}
                value={value as string}
                id={id}
                disabled={disabled}
                aria-disabled={disabled}
            >
                <RadioGroupPrimitive.Indicator
                    css={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        height: '100%',
                        position: 'relative',
                        transition: 'all 0.3s',
                        '&::after': {
                            content: '""',
                            display: 'block',
                            width: `${innerSize} !important`,
                            height: `${innerSize} !important`,
                            borderRadius: '50%',
                            backgroundColor: colors.primaryOrange,
                        },
                    }}
                />
            </RadioGroupPrimitive.Item>
            <label
                css={{
                    lineHeight: 1,
                    userSelect: 'none',
                    paddingLeft: '8px',
                }}
                htmlFor={id}
            >
                {children}
            </label>
        </div>
    );
};

export interface RadioGroupBaseProps
    extends Omit<HtmlPropsNoRef<HTMLInputElement>, 'value'> {
    /**
* Name of the Radio - will be used for the form validation if using FormContext so make sure it's unique.
*/
    name: string;
    /**
* Children refers to the label
* */
    children: any;
    /**
* Make the field required or not. Defaults to false for all field types.
*/
    required?: boolean;
    /**
* This refers to the value of the input
*/
    value?: string | boolean;
    // eslint-disable-next-line react/no-unused-prop-types
    formContext?: any;
}

const RadioGroupBase = ({
    children,
    onChange,
    name,
    required,
    value,
    formContext,
}: RadioGroupBaseProps) : JSX.Element => {
    const id = usePersistedId();
    if (!Array.isArray(children)) {
        // eslint-disable-next-line no-param-reassign
        children = [children];
    }

    return (
        <div css={{
            position: 'relative',
        }}
        >
            <RadioGroupPrimitive.Root
                onValueChange={(e) => {
                    onChange(e);
                }}
                value={value}
                name={name}
                id={id}
                css={{
                    position: 'relative',
                    display: 'flex',
                    gap: '24px',
                    padding: formContext
                    && formContext.errors
                    && formContext.errors[name] ? '12px' : '0',
                    fontSize: '16px',
                    borderRadius: '4px',
                    border: formContext
                    && formContext.errors
                    && formContext.errors[name] ? `2px solid ${colors.statesError}` : '2px solid transparent',
                }}
            >
                {required && (
                    <label
                        htmlFor={id}
                        css={{
                            position: 'absolute',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            right: '-18px',
                            top: '12px',
                        }}
                    >
                        <span
                            css={{
                                fontSize: '14px',
                                paddingTop: '5px',
                                borderRadius: '999px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '8px',
                                width: '16px',
                                color:
                                    formContext && formContext.errors && formContext.errors[name]
                                        ? colors.statesError
                                        : 'currentColor',
                                transition: 'color 0.2s',
                                marginTop: '1px',
                            }}
                        >
                            *
                            {' '}
                            <VisuallyHidden>This field is required.</VisuallyHidden>
                        </span>
                    </label>
                )}
                {children}
            </RadioGroupPrimitive.Root>
            {formContext
                && formContext.errors
                && formContext.errors[name] && (
                <>
                    {formContext.errors[name].type === 'validate' && (
                        <FormAlert>Required</FormAlert>
                    )}
                </>
            )}
        </div>
    );
};

const RadioGroup = ({
    children,
    value,
    onChange,
    name,
    required = false,
}: RadioGroupBaseProps) : JSX.Element => {
    const formContext = useFormProvider();

    if (formContext) {
        formContext.register(name, { validate: required ? (x: any) => x !== null : {} });

        return (
            <RadioGroupBase
                value={formContext?.getValues(name)}
                onChange={(e) => {
                    formContext.setValue(name, e);
                    if (onChange) {
                        onChange(e);
                    }
                }}
                name={name}
                required={required}
                formContext={formContext}
            >
                {children}
            </RadioGroupBase>
        );
    }
    return (
        <RadioGroupBase value={value} onChange={onChange} name={name}>
            {children}
        </RadioGroupBase>
    );
};

export default RadioGroup;
